import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createContact,
    deleteContact,
    getAdminContactById,
    getAdminContacts,
    updateContact,
} from '../apis/adminContacts';

export const adminContactKeys = {
    all: ['admin', 'contacts'],
    lists: () => [...adminContactKeys.all, 'list'],
    list: (params) => [...adminContactKeys.lists(), params],
    details: () => [...adminContactKeys.all, 'detail'],
    detail: (id) => [...adminContactKeys.details(), id],
};

const normalizeContactList = (response, params) => ({
    items: response?.data?.items || [],
    total: response?.data?.totalItems || 0,
    currentPage: response?.data?.pageIndex || response?.data?.currentPage || params.pageNumber || 1,
    pageSize: response?.data?.pageSize || params.pageSize || 10,
});

const normalizeContactDetail = (response) => {
    if (!response?.success || !response.data) return null;

    return {
        ...response.data,
        translation: response.data.translations || response.data.translation || [],
    };
};

export const useAdminContactsQuery = (params) => (
    useQuery({
        queryKey: adminContactKeys.list(params),
        queryFn: () => getAdminContacts(params),
        select: (response) => normalizeContactList(response, params),
        placeholderData: keepPreviousData,
    })
);

export const useAdminContactQuery = (id) => (
    useQuery({
        queryKey: adminContactKeys.detail(id),
        queryFn: () => getAdminContactById(id),
        enabled: Boolean(id),
        select: normalizeContactDetail,
    })
);

export const useCreateContactMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createContact,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: adminContactKeys.lists() }),
    });
};

export const useUpdateContactMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }) => updateContact(id, data),
        onSuccess: (_response, variables) => {
            queryClient.invalidateQueries({ queryKey: adminContactKeys.lists() });
            queryClient.invalidateQueries({ queryKey: adminContactKeys.detail(variables.id) });
        },
    });
};

export const useDeleteContactMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteContact,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: adminContactKeys.lists() }),
    });
};
